import React, { useState } from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { RadioGroup, RadioGroupItem } from "@shadcn-in-nx/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@shadcn-in-nx/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@shadcn-in-nx/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Clock, Check, ChevronsUpDown } from "lucide-react";
import moment from "moment-timezone";
import { toast } from "sonner";

// Example list of timezones - consider using a library like moment-timezone for a full list
const TIMEZONES = moment.tz.names();

const WEEKDAYS = [
    "Sunday", // Changed order to match common calendar start
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

export default function ScheduleTriggerModal({ isOpen, onClose, onConfirm }) {
    const [scheduleMode, setScheduleMode] = useState("weekdays"); // 'weekdays', 'daysOfMonth', 'recurring'
    const [timeOfDay, setTimeOfDay] = useState("09:00");
    const [selectedWeekdays, setSelectedWeekdays] = useState([]);
    const [selectedDays, setSelectedDays] = useState(""); // Store as string to preserve commas
    const [recurringValue, setRecurringValue] = useState(1);
    const [recurringUnit, setRecurringUnit] = useState("Hours"); // 'Minutes', 'Hours'
    const [timezone, setTimezone] = useState("America/New_York"); // Default timezone
    const [popoverOpen, setPopoverOpen] = useState(false);

    const handleWeekdayChange = (value) => {
        setSelectedWeekdays((prev) => {
            if (prev.includes(value)) {
                return prev.filter((day) => day !== value);
            }
            return [...prev, value];
        });
        setSelectedDays([]); // Clear days of month when weekdays are selected
    };

    const handleModeChange = (value) => {
        setScheduleMode(value);
        // Clear the other selection when changing modes
        if (value === "weekdays") {
            setSelectedDays("");
        } else if (value === "daysOfMonth") {
            setSelectedWeekdays([]);
        }
    };

    const handleConfirm = () => {
        console.log("handleConfirm called. scheduleMode:", scheduleMode); // Debug log
        // Validate time format
        if (
            !timeOfDay ||
            !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeOfDay)
        ) {
            toast.error(
                "Please enter a valid time in HH:MM format (e.g., 09:00, 14:30)"
            );
            return;
        }

        let configPayload = {};
        let daysOfMonthParsed = [];

        if (scheduleMode === "daysOfMonth") {
            // Validate days of month - parse the string input
            daysOfMonthParsed = selectedDays
                .split(",")
                .map((d) => parseInt(d.trim()))
                .filter((d) => !isNaN(d) && d >= 1 && d <= 31);

            if (
                daysOfMonthParsed.length === 0 &&
                selectedDays.trim().length > 0
            ) {
                toast.error(
                    "Please enter valid days (1-31) separated by commas."
                );
                return;
            }
            if (daysOfMonthParsed.length === 0) {
                toast.error("Please select at least one day of the month.");
                return;
            }

            configPayload = {
                timeOfDay,
                timezone,
                daysOfMonth: daysOfMonthParsed,
                weekdays: [], // Ensure weekdays is empty
            };
        } else if (scheduleMode === "recurring") {
            if (!recurringValue || recurringValue <= 0) {
                toast.error("Please enter a valid recurring value.");
                return;
            }

            let cronExpression = "";
            if (recurringUnit === "Minutes") {
                cronExpression = `*/${recurringValue} * * * *`;
            } else {
                // Hours
                cronExpression = `0 */${recurringValue} * * *`;
            }

            configPayload = {
                cron: cronExpression,
                timezone,
            };
        } else {
            // scheduleMode === 'weekdays'
            if (selectedWeekdays.length === 0) {
                toast.error("Please select at least one day of the week.");
                return;
            }
            configPayload = {
                timeOfDay,
                timezone,
                weekdays: selectedWeekdays,
                daysOfMonth: [], // Ensure daysOfMonth is empty
            };
        }

        console.log("Calling onConfirm with payload:", configPayload);
        onConfirm(configPayload);
    };

    const handleCancel = () => {
        // Reset form state
        setScheduleMode("weekdays");
        setTimeOfDay("09:00");
        setSelectedWeekdays([]);
        setSelectedDays("");
        setRecurringValue(1);
        setRecurringUnit("Hours");
        setTimezone("America/New_York");
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto z-50">
                <DialogHeader>
                    <DialogTitle>Configure Schedule Trigger</DialogTitle>
                    <DialogDescription>
                        Set up when this workflow should run automatically.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="space-y-2">
                        <Label>How often should this run?</Label>
                        <RadioGroup
                            value={scheduleMode}
                            onValueChange={handleModeChange}
                            className="flex space-x-4"
                        >
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                    value="weekdays"
                                    id="weekdays"
                                />
                                <Label htmlFor="weekdays">Weekly</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                    value="daysOfMonth"
                                    id="daysOfMonth"
                                />
                                <Label htmlFor="daysOfMonth">Monthly</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                    value="recurring"
                                    id="recurring"
                                />
                                <Label htmlFor="recurring">Recurring</Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            {scheduleMode === "recurring" ? (
                                <>
                                    <Label htmlFor="recurringValue">
                                        Every
                                    </Label>
                                    <div className="flex space-x-2">
                                        <Input
                                            id="recurringValue"
                                            type="number"
                                            min="1"
                                            value={recurringValue}
                                            onChange={(e) =>
                                                setRecurringValue(
                                                    parseInt(e.target.value)
                                                )
                                            }
                                            className="w-20"
                                        />
                                        <Select
                                            value={recurringUnit}
                                            onValueChange={setRecurringUnit}
                                        >
                                            <SelectTrigger className="w-32">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Minutes">
                                                    Minutes
                                                </SelectItem>
                                                <SelectItem value="Hours">
                                                    Hours
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Label htmlFor="time">Time of Day</Label>
                                    <Input
                                        id="time"
                                        type="time"
                                        value={timeOfDay}
                                        onChange={(e) =>
                                            setTimeOfDay(e.target.value)
                                        }
                                    />
                                </>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="timezone">Timezone</Label>
                            <Popover
                                open={popoverOpen}
                                onOpenChange={setPopoverOpen}
                                modal={false}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={popoverOpen}
                                        className="w-full justify-between"
                                        id="timezone"
                                    >
                                        {timezone
                                            ? TIMEZONES.find(
                                                  (tz) => tz === timezone
                                              )
                                            : "Select timezone..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                                    <Command>
                                        <CommandInput placeholder="Search timezone..." />
                                        <CommandList>
                                            <CommandEmpty>
                                                No timezone found.
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {TIMEZONES.map((tz) => (
                                                    <CommandItem
                                                        key={tz}
                                                        value={tz}
                                                        onSelect={() => {
                                                            setTimezone(tz);
                                                            setPopoverOpen(
                                                                false
                                                            );
                                                        }}
                                                    >
                                                        <Check
                                                            className={`mr-2 h-4 w-4 ${
                                                                timezone === tz
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            }`}
                                                        />
                                                        {tz}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    {scheduleMode === "weekdays" && (
                        <div className="space-y-2">
                            <Label>On these days of the week</Label>
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {WEEKDAYS.map((day) => (
                                    <label
                                        key={day}
                                        className="flex items-center space-x-2 p-2 border rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedWeekdays.includes(
                                                day
                                            )}
                                            onChange={() =>
                                                handleWeekdayChange(day)
                                            }
                                            className="h-4 w-4"
                                        />
                                        <span>{day}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Select one or more days. The workflow will run
                                weekly on the selected days at the specified
                                time.
                            </p>
                        </div>
                    )}

                    {scheduleMode === "daysOfMonth" && (
                        <div className="space-y-2">
                            <Label>On these dates of the month</Label>
                            <Input
                                type="text"
                                placeholder="e.g. 1, 15, 31"
                                value={selectedDays}
                                onChange={(e) => {
                                    setSelectedDays(e.target.value);
                                }}
                            />
                            <p className="text-sm text-muted-foreground">
                                Enter comma-separated dates (1-31). The workflow
                                will run monthly on the selected dates at the
                                specified time.
                            </p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => {
                            handleConfirm();
                        }}
                    >
                        <Clock className="w-4 h-4 mr-2" />
                        Create Schedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
